import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { CATEGORY_LABELS, formatDate, readingTime } from "@/lib/format";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ArticleCard, ArticleRow } from "@/components/site/ArticleCard";
import { Meistgelesen } from "@/components/site/Meistgelesen";
import { Kicker } from "@/components/site/BreakingTag";
import { toast } from "sonner";

function SidebarNewsletter() {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/newsletter", { email }); toast.success(data.message || "Angemeldet."); setEmail(""); }
    catch { toast.error("Anmeldung fehlgeschlagen."); }
  };
  return (
    <div className="bg-prussian text-white p-6">
      <h2 className="font-headline font-bold text-xl mb-2">Der Morgen-Newsletter</h2>
      <p className="text-sm text-white/70 font-body mb-4">Kostenlos. Jeden Werktag um 7 Uhr die Analyse der Redaktion.</p>
      <form onSubmit={submit} className="flex flex-col gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="sidebar-newsletter-input" placeholder="E-Mail-Adresse" className="bg-white text-charcoal px-3 py-2.5 text-sm outline-none" />
        <button data-testid="sidebar-newsletter-submit" className="border border-white/50 text-white font-semibold text-xs uppercase tracking-wide py-2.5 hover:bg-white hover:text-prussian transition-colors">Abonnieren</button>
      </form>
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [latest, setLatest] = useState([]);
  const [mostRead, setMostRead] = useState([]);

  useEffect(() => {
    api.get("/articles/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/articles/most-read", { params: { limit: 5 } }).then((r) => setMostRead(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!featured) return;
    api.get("/articles/latest", { params: { limit: 9, exclude: featured.id } })
      .then((r) => setLatest(r.data)).catch(() => {});
  }, [featured]);

  return (
    <SiteLayout>
      <div className="max-w-[1240px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main column */}
          <div className="lg:col-span-2">
            {/* Hero Une */}
            {featured && (
              <article data-testid="hero-une-article" className="group border-b-2 border-prussian pb-8 mb-8">
                <Link to={`/artikel/${featured.slug}`} className="block overflow-hidden bg-edborder/40">
                  <img src={resolveImg(featured.featured_image)} alt={featured.title}
                    className="w-full aspect-[16/9] object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
                </Link>
                <div className="flex items-center gap-2 mt-4">
                  <Kicker breaking={featured.is_breaking}>{featured.kicker || CATEGORY_LABELS[featured.category]}</Kicker>
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-body">{CATEGORY_LABELS[featured.category]}</span>
                </div>
                <Link to={`/artikel/${featured.slug}`}>
                  <h1 className="font-headline font-black text-prussian text-3xl md:text-5xl leading-[1.05] mt-2 group-hover:text-gold-hover transition-colors">
                    {featured.title}
                  </h1>
                </Link>
                <p className="mt-4 font-headline text-lg md:text-xl text-charcoal/80 leading-relaxed italic">
                  {featured.chapo}
                </p>
                <div className="mt-4 text-xs text-muted-foreground font-body uppercase tracking-wide">
                  Von {featured.author} · {formatDate(featured.published_at)}
                </div>
              </article>
            )}

            {/* Secondary grid */}
            <div data-testid="secondary-articles-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {latest.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-8">
              {mostRead.length > 0 && <Meistgelesen articles={mostRead} />}
              <SidebarNewsletter />
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
